import { FC, KeyboardEvent, ReactNode } from 'react';

import classNames from 'classnames';

import dataAttributes from '../../../../utils/attributes/attributes';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Typography } from '../../../typography/components/Typography/Typography';

interface Props {
   type?: ReactNode;
   label?: ReactNode;
   leadingIcon?: string;
   disabled?: boolean;
   fullWidth?: boolean;
   attributes?: Record<string, string>;
   onClick?: () => void;
   onDelete?: () => void;
}

export const Chip: FC<Props> = ({ type, label, leadingIcon, onClick, onDelete, disabled = false, fullWidth = false, attributes }) => {
   const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (!onClick) return;
      if (e.key === 'Enter' || e.key === ' ') {
         e.preventDefault();
         onClick();
      }
   };

   return (
       
      <div
         {...dataAttributes(attributes)}
         data-testid={`chip${onClick ? '--clickable' : ''}${disabled ? '--disabled' : ''}`}
         role={onClick ? 'button' : 'presentation'}
          
         tabIndex={onClick ? 0 : undefined}
         onClick={onClick}
         onKeyDown={handleKeyDown}
         className={classNames(
            'rounded-component-data-display-chip-container-border-radius',
            'border-component-data-display-chip-container-border-width-default',
            'border-component-data-display-chip-container-color-border-enabled',
            'focus-visible:border-component-data-display-chip-container-color-border-focused',
            'py-component-data-display-chip-container-spacing-padding-y',
            'min-h-component-data-display-chip-container-sizing-min-height',
            'gap-component-data-display-chip-container-spacing-gap',
            'inline-flex items-center',
            onClick ? 'cursor-pointer' : 'cursor-default',
            {
               'pl-component-data-display-chip-container-spacing-padding-x-no-icon': !leadingIcon,
               'pl-component-data-display-chip-container-spacing-padding-x-icon': leadingIcon,
               'pr-component-data-display-chip-container-spacing-padding-x-no-icon': !onDelete,
               'pr-component-data-display-chip-container-spacing-padding-x-icon': onDelete,
               'bg-component-data-display-chip-container-color-background-disabled': disabled,
               'bg-component-data-display-chip-container-color-background-enabled': !disabled,
               'hover:bg-component-data-display-chip-container-color-background-hover focus:bg-component-data-display-chip-container-color-background-focused focus:border-component-data-display-chip-container-color-border-focused active:bg-component-data-display-chip-container-color-background-pressed':
                  !disabled && (onClick || onDelete),
               'w-full': fullWidth
            }
         )}
      >
         {leadingIcon && (
            <div data-testid="chip--leading-icon" className={classNames(disabled && 'text-component-data-display-chip-icon-right-color-fill-on-disabled', !disabled && 'text-component-data-display-chip-icon-right-color-fill-on-enabled')}>
               <Icon icon={leadingIcon} />
            </div>
         )}
         {type && (
            <span data-testid="chip--type" className={classNames(disabled && 'text-component-data-display-chip-label-icon-end-color-text-on-disabled', !disabled && 'text-component-data-display-chip-label-icon-end-color-text-on-surface')}>
               <Typography color="inherit" variant="small-highlight">
                  {type}
               </Typography>
            </span>
         )}
         {label && (
            <span data-testid="chip--label" className={classNames(disabled && 'text-component-data-display-chip-label-color-text-on-disabled', !disabled && 'text-component-data-display-chip-label-color-text-on-enabled')}>
               <Typography color="inherit" variant="small">
                  {label}
               </Typography>
            </span>
         )}
         {onDelete && (
            <button data-testid="chip--remove" type="button" onClick={onDelete} disabled={disabled} className={classNames(disabled && 'text-component-data-display-chip-label-icon-end-color-text-on-disabled', !disabled && 'text-component-data-display-chip-label-icon-end-color-text-on-surface')}>
               <Icon icon="times" size="small" color="subtle" />
            </button>
         )}
      </div>
   );
};
